import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Ref, SimpleEvent } from "@eastsideco/salvo-ts";
import {
    TextFacetValue,
    ColorFacetValue,
    isRangeFacet,
    isColorFacet,
    type SearchPage,
    type LandingPage,
    type Facet,
    type RangeFacet,
    type FacetParams,
    type SizeType,
    type SortType,
    type SizeFormat
} from '@apptus/esales-api';
import { FakeDropdown } from "../FakeDropdown/FakeDropdown";
import { VoyadoPriceFilters } from "./VoyadoPriceFilters";

export interface FilterValues {
    sortBy: SortType;
    facetParams: FacetParams;
}

@Component
export class VoyadoFilters extends ThemeComponent {
    @Ref filters!: HTMLSpanElement;
    @Ref chevron!: HTMLDivElement;
    @Ref() closeFilters!: HTMLButtonElement[];
    @Ref({ required: false }) group!: HTMLElement[];
    @Ref sortBy!: FakeDropdown;
    @Ref sortByMobile!: FakeDropdown;

    private isLoaded = false;
    private result?: LandingPage | SearchPage;
    private facetParams: FacetParams = {};

    init() {
        this.addSortByListeners();
        this.addFiltersListener();

        this.closeFilterMenu(this.closeFilters);
    }

    private _onChange = new SimpleEvent<FilterValues>();

    public get onChange() {
        return this._onChange.asEvent();
    }

    get values() {
        return {
            sortBy: this.sortBy.input.value as SortType,
            facetParams: this.facetParams
        };
    }

    get chevronHTML() {
        const icon = this.chevron.querySelector('svg');
        return icon?.outerHTML;
    }

    setResult(result: LandingPage | SearchPage, force = false) {
        // Only render new results the first time unless force is set to true.
        if (this.isLoaded && !force) {
            return;
        }

        this.result = result;
        this.isLoaded = true;

        this.showFacets();
    }

    addSortByListeners() {
        this.$subscribe(this.sortBy.onChange, () => {
            this._onChange.dispatch(this.values);
        });
    }

    addFiltersListener() {
        this.$listen(this, 'change', (event) => {
            if (! (event.target as HTMLElement).matches('[data-ref=filter]')) {
                return;
            }

            this.updateFilters();
            this.updateFilterCounts();
        });
    }

    updateFilters() {
        const filters = this.querySelectorAll<HTMLInputElement>('[data-ref=filter]');

        this.facetParams = [...filters].reduce((values: FacetParams, filter) => {
            const key = filter.name.replace('[]', '');
            const value = filter.value;

            if (filter.checked) {
                const currentValues = values[key] || [];

                return {
                    ...values,
                    [key]: [
                        ...(currentValues as string[]),
                        value
                    ],
                };
            }

            return values;
        }, {});

        const priceFiltersComponent = this.querySelector('voyado-price-filters') as VoyadoPriceFilters;

        if (priceFiltersComponent && priceFiltersComponent?.range && priceFiltersComponent.isActive) {
            const priceFacetParams = {
                [priceFiltersComponent?.range.id]: priceFiltersComponent.selectedRange
            };

            this.facetParams = {
                ...this.facetParams,
                ...priceFacetParams,
            };
        }

        this._onChange.dispatch(this.values);
    }

    updateFilterCounts() {
        const groups = this.querySelectorAll('[data-ref=group]');

        for (const group of groups) {
            const inputs = group.querySelectorAll('input[type=checkbox]:checked');
            const count = group.querySelector<HTMLSpanElement>('.voyado-filters__active-filter-count');

            if (count) {
                count.innerText = inputs.length ? inputs.length.toString() : '';
            }
        }
    }

    showFacets() {
        if (! this.result?.primaryList?.facets) {
            return;
        }

        const facetsHTML = this.result?.primaryList?.facets.map((facet: Facet) => `
          <div class="voyado-filters__group collection-filters__group--open" data-ref="group">
            <div class="voyado-filters__group-summary">
              <div class="voyado-filters__group-label">
                ${facet.label}
              </div>

              <span class="voyado-filters__active-filter-count"></span>

              ${this.chevronHTML}
            </div>

            <div class="voyado-filters__group-options">
              ${this.renderFacetSelector(facet)}
            </div>
          </div>
        `).join('');

        this.filters.innerHTML = facetsHTML;

        this.renderPriceFilters(
            this.result?.primaryList?.facets.find((facet: Facet) => facet && isRangeFacet(facet)) as RangeFacet
        );

        this.handleGroupOpen();
    }

    renderFacetSelector(facet: Facet) {
        // @ts-expect-error Error for Facet types without values.
        const values = facet.values;
        // @ts-expect-error Error for Facet types without sizeTypes.
        const sizeTypes = facet.sizeTypes;

        if (values) {
            const valuesHTML = values.map(
                (value: TextFacetValue | ColorFacetValue) => this.renderFacetValue(facet, value)
            ).join('');

            return `
              <ul class="voyado-filters__group-options__list">
                  ${valuesHTML}
              </ul>
            `;
        } else if (sizeTypes) {
            const sizesHTML = sizeTypes.map((sizeType: SizeType) => sizeType.formats.map(
                (sizeFormat: SizeFormat) =>
                    sizeFormat.values.map((value) => this.renderFacetValue(facet, value)).join('')
            ).join('')).join('');

            return `
                <ul class="voyado-filters__group-options__list">
                    ${sizesHTML}
                </ul>
            `;
        } else if (facet.type === 'RANGE') {
            return `
                <div data-ref="priceFilters" data-name="${facet.id}"></div>
            `;
        }
    }

    renderFacetValue(facet: Facet, value: TextFacetValue | ColorFacetValue) {
        let swatchHTML = '';
        const colorValue = (value as ColorFacetValue);

        if (colorValue.color) {
            swatchHTML = `
                <div class="voyado-filters__swatch" style="--swatch-color: ${colorValue.color}">
                </div>
            `;
        }

        return `
            <li class="voyado-filters__group-options__list-item">
                <div class="form-checkbox">
                    <label class="form-checkbox__label flex f-vertical-center">
                        <span class="checkmark"></span>
                        <input 
                            type="checkbox" 
                            data-ref="filter"
                            name="${facet.id}[]"
                            value="${value.id}">
                        
                        ${swatchHTML ? swatchHTML : ''}
                        
                        ${value.label}
                    </label>
                </div>
            </li>
        `;
    }

    renderPriceFilters(facet?: RangeFacet) {
        const target = this.querySelector('[data-ref=priceFilters]');

        if (!target || ! facet) {
            return;
        }

        const priceFilters = new VoyadoPriceFilters;
        priceFilters.range = facet;

        target.replaceChildren(priceFilters);

        this.$subscribe(priceFilters.onChange, () => {
            this.updateFilters();
        });
    }


    private handleGroupOpen() {
        const groups = this.querySelectorAll<HTMLElement>('[data-ref=group]');

        for (const group of groups) {
            this.$listen(group, 'click', (event) => {
                const clickedElement = event.target as HTMLElement;
                const groupOptions = group.querySelector('.voyado-filters__group-options');

                if (groupOptions && (clickedElement == groupOptions || groupOptions.contains(clickedElement))) {
                    return;
                }

                for (const sibling of groups) {
                    if (group !== sibling) {
                        sibling.classList.remove('voyado-filters__group--open');
                    }
                }

                group.classList.toggle('voyado-filters__group--open');
            });
        }
    }

    private closeFilterMenu(closeFilters: HTMLButtonElement[]) {
        for (const closeFilter of closeFilters) {
            this.$listen(closeFilter, 'click', () => {
                // Only run if filters are active
                if (!document.body.classList.contains('collection-filters-active')) {
                    return;
                }

                // Body class
                document.body.classList.remove('collection-filters-active');

                // Filters class
                this.classList.remove('voyado-filters--active');
            });
        }
    }
}
